import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import i18n from "src/i18n";
import {
  mapFoodicsInventoryItemToImportPayload,
  useFoodicsMenuEntities,
  useImportFoodicsMenuEntities,
} from "src/api/integrations/foodics";
import { isProductionItem } from "src/types/foodics";
import type { FoodicsInventoryItemEntity } from "src/types/foodics";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import { StepIndicatorCard } from "../../step-indicator-card";
import { ImportModeCard } from "../../import-mode-card";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";

const FoodicsProductionImportEdit = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [withRecipe, setWithRecipe] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(100);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Ids already defaulted to selected once — unchecking must survive page changes.
  const seenIdsRef = useRef<Set<string>>(new Set());
  // Row payloads must stay available for import even after paging away.
  const rowsCacheRef = useRef<Map<string, FoodicsInventoryItemEntity>>(
    new Map()
  );
  const isArabic = i18n.language === "ar";
  const currentLang = i18n.language || "en";

  const { data, isLoading } = useFoodicsMenuEntities({
    include_inventory: 1,
    inventory_page: page + 1,
    inventory_per_page: perPage,
    products_per_page: 1,
    categories_per_page: 1,
  });
  const { mutate: importItems, status: importStatus } =
    useImportFoodicsMenuEntities();

  const fetchFailed = data?.data?.inventory_fetch_failed === true;
  const pagination = data?.data?.inventory_pagination ?? null;
  // Production items are items with ingredients.
  const productionItems = (data?.data?.inventory_items ?? []).filter(
    (row) => isProductionItem(row)
  );

  const getLocalized = (text?: { en?: string; ar?: string }): string =>
    text?.[currentLang as "en" | "ar"] || text?.en || text?.ar || "-";

  // All production items are selected by default (screenshot behavior)
  useEffect(() => {
    const newIds: string[] = [];
    productionItems.forEach((row) => {
      if (!row.external_id) return;
      rowsCacheRef.current.set(row.external_id, row);
      if (!seenIdsRef.current.has(row.external_id)) {
        seenIdsRef.current.add(row.external_id);
        if (!row.already_imported) {
          newIds.push(row.external_id);
        }
      }
    });
    if (newIds.length > 0) {
      setSelectedIds((prev) => [...prev, ...newIds]);
    }
  }, [productionItems]);

  useEffect(() => {
    if (importStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("foodicsWorkspace.productionImport.importFinished"),
        },
      ]);
      navigate("/integrations/foodics");
    } else if (importStatus === "error") {
      setNotifications([
        ...notifications,
        { type: "error", message: t("menuImport.importError") },
      ]);
    }
  }, [importStatus]);

  const toggleItem = (id: string | null) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectableVisibleIds = productionItems
    .filter((row) => !row.already_imported && row.external_id)
    .map((row) => row.external_id as string);
  const allVisibleSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = selectableVisibleIds.some((id) =>
    selectedIds.includes(id)
  );

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !selectableVisibleIds.includes(id))
      );
      return;
    }
    setSelectedIds((prev) => [
      ...prev,
      ...selectableVisibleIds.filter((id) => !prev.includes(id)),
    ]);
  };

  const handleImportAndFinish = () => {
    const payload = selectedIds
      .map((id) => rowsCacheRef.current.get(id))
      .filter(
        (row): row is FoodicsInventoryItemEntity =>
          !!row && !!row.external_id && !row.already_imported
      )
      .map((row) =>
        mapFoodicsInventoryItemToImportPayload(row, {
          includeIngredients: withRecipe,
        })
      );

    if (payload.length === 0) {
      navigate("/integrations/foodics");
      return;
    }
    importItems({
      categories: [],
      products: [],
      inventory_items: payload,
    });
  };

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      {/* Header */}
      <Box>
        <Button
          variant="text"
          startIcon={
            <ArrowBackIcon
              sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
            />
          }
          onClick={() => navigate("/integrations/foodics")}
          sx={{ color: "#667085" }}
        >
          {t("foodicsWorkspace.editImport.backToWorkspace")}
        </Button>
        <Typography fontSize={24} fontWeight={600} color="#101828" mt={1}>
          {t("foodicsWorkspace.productionImport.title")}
        </Typography>
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.productionImport.subtitle")}
        </Typography>
      </Box>

      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {/* Step indicator */}
      <StepIndicatorCard
        stepNumber={1}
        icon={<SoupKitchenIcon />}
        title={t("foodicsWorkspace.productionImport.stepTitle")}
        subtitle={t("foodicsWorkspace.productionImport.stepSubtitle")}
        isActive
        isCompleted={false}
      />

      {/* Import mode chooser */}
      <Box>
        <Typography fontSize={16} fontWeight={600} color="#101828">
          {t("foodicsWorkspace.productionImport.modeQuestion")}
        </Typography>
        <Typography fontSize={12} color="#667085" mb={1.5}>
          {t("foodicsWorkspace.productionImport.modeHint")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <ImportModeCard
            icon={<MenuBookIcon fontSize="small" />}
            iconBgcolor="#FF8D3F"
            iconColor="#FFFFFF"
            title={t("foodicsWorkspace.productionImport.withRecipeTitle")}
            subtitle={t(
              "foodicsWorkspace.productionImport.withRecipeSubtitle"
            )}
            isSelected={withRecipe}
            onSelect={() => setWithRecipe(true)}
          />
          <ImportModeCard
            icon={<BlockIcon fontSize="small" />}
            iconBgcolor="#F2F4F7"
            iconColor="#667085"
            title={t("foodicsWorkspace.productionImport.withoutRecipeTitle")}
            subtitle={t(
              "foodicsWorkspace.productionImport.withoutRecipeSubtitle"
            )}
            isSelected={!withRecipe}
            onSelect={() => setWithRecipe(false)}
          />
        </Stack>
      </Box>

      {/* Production items table */}
      <Box>
        <Typography fontSize={16} fontWeight={600} color="#101828">
          {t("foodicsWorkspace.productionImport.stepTitle")}
        </Typography>
        <Typography fontSize={12} color="#667085" mb={1.5}>
          {t("foodicsWorkspace.productionImport.itemsHint")}
        </Typography>

        {fetchFailed && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {t("menuImport.inventoryFetchFailed")}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    disabled={selectableVisibleIds.length === 0}
                  />
                </TableCell>
                {[
                  t("menuImport.table.sku"),
                  t("menuImport.table.name"),
                  t("menuImport.table.recipeQty"),
                ].map((item) => (
                  <TableCell
                    key={item}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      color: "#475467",
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableLoadingSkeleton rowsLength={4} cellsLength={4} />
            ) : (
              <TableBody>
                {productionItems.map((row, index) => (
                  <TableRow key={row.external_id ?? `prod-${index}-${row.sku}`}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          !!row.external_id &&
                          selectedIds.includes(row.external_id)
                        }
                        onChange={() => toggleItem(row.external_id)}
                        disabled={row.already_imported || !row.external_id}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={14} fontWeight={600}>
                        {row.sku || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontSize={14} fontWeight={600}>
                          {getLocalized(row.name)}
                        </Typography>
                        {row.already_imported && (
                          <Chip
                            size="small"
                            color="success"
                            label={t("menuImport.imported")}
                          />
                        )}
                        {!row.already_imported && row.is_new && (
                          <Chip
                            size="small"
                            color="warning"
                            label={t("menuImport.new")}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{row.storage_to_ingredient_factor ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {!isLoading && productionItems.length === 0 && <NoData />}
        </TableContainer>

        <TablePagination
          component="div"
          labelRowsPerPage={generalT("rowsPerPage")}
          count={pagination?.total ?? productionItems.length}
          page={Math.max((pagination?.current ?? page + 1) - 1, 0)}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pagination?.per ?? perPage}
          onRowsPerPageChange={(event) => {
            const value = parseInt(event.target.value, 10);
            setPerPage(value);
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Box>

      {/* Footer */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        pt={2}
        borderTop="1px solid #EAECF0"
      >
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.editImport.selectedOf", {
            selected: selectedIds.length,
            total: seenIdsRef.current.size,
          })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="tertiary"
            onClick={() => navigate("/integrations/foodics")}
            disabled={importStatus === "pending"}
          >
            {generalT("cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={importStatus === "pending"}
            startIcon={
              importStatus === "pending" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckIcon fontSize="small" />
              )
            }
            endIcon={
              <ArrowForwardIcon
                sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
                fontSize="small"
              />
            }
            onClick={handleImportAndFinish}
          >
            {t("foodicsWorkspace.editImport.importAndFinish")}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export default FoodicsProductionImportEdit;
